import React, { useCallback, useEffect, useState } from "react";
import {
  Segment,
  Header,
  Table,
  Input,
  Button,
  Loader,
  Select,
  Message,
  Modal,
} from "semantic-ui-react";
import { getApiLogs } from "../../../../../apis/apiLogsApi";
import { useFilters, notEmptyValidator } from "../../../../../hooks";
import { usePagination } from "../../../../../hooks/usePagination.hook";
import { getDateFormat } from "../../../../../common/date-format";

// Define the table columns for the API logs matching the backend schema
const columns = [
  {
    label: "API Key",
    prop: "apiKey",
    width: 2,
    getValue: (apiKey) =>
      apiKey && apiKey.name
        ? `${apiKey.name} (${apiKey.first4}...${apiKey.last4})`
        : "—",
  },
  {
    label: "Path",
    prop: "path",
    width: 2,
  },
  {
    label: "Method",
    prop: "method",
    width: 1,
  },
  {
    label: "Status Code",
    prop: "statusCode",
    width: 1,
  },
  {
    label: "IP Address",
    prop: "ip",
    width: 1,
  },
  {
    label: "Response Time (ms)",
    prop: "responseTimeMs",
    width: 1,
  },
  {
    label: "Created At",
    prop: "createdAt",
    width: 2,
    getValue: (fieldValue) => (fieldValue ? getDateFormat(fieldValue) : ""),
  },
];

// Define the HTTP method options for filtering
const METHOD_OPTIONS = [
  { key: "GET", text: "GET", value: "GET" },
  { key: "POST", text: "POST", value: "POST" },
  { key: "PUT", text: "PUT", value: "PUT" },
  { key: "DELETE", text: "DELETE", value: "DELETE" },
  { key: "PATCH", text: "PATCH", value: "PATCH" },
];

// Define the status code options for filtering
const STATUS_CODE_OPTIONS = [
  { key: "200-299", text: "Success (200-299)", value: "200-299" },
  { key: "300-399", text: "Redirect (300-399)", value: "300-399" },
  { key: "400-499", text: "Client Error (400-499)", value: "400-499" },
  { key: "500-599", text: "Server Error (500-599)", value: "500-599" },
];

const APIAccessLog = () => {
  // State for detail modal
  const [detailModalOpen, setDetailModalOpen] = useState(false);
  const [selectedLog, setSelectedLog] = useState(null);

  // Setup filters for the API logs
  const { filters, normalizedFilters, setFormFilter, resetFilters } =
    useFilters(
      {
        search: "",
        method: [],
        statusCode: [],
        dateFrom: "",
        dateTo: "",
      },
      {
        localStorageKey: "api-logs",
        filterValidators: {
          search: notEmptyValidator(),
        },
        filterDebounces: {
          search: 700,
        },
        temporaryFilters: ["search"],
      },
    );

  // Setup pagination for the API logs
  const {
    items: logs,
    Paginator,
    loadItems: loadLogs,
    refreshItems: refreshLogs,
    isLoading,
    totalItems,
  } = usePagination(getApiLogs, {
    initialLoad: true,
    clearOnLoad: true,
    dataProperty: "items",
  });

  // Load logs when filters change
  useEffect(() => {
    loadLogs(1, { ...normalizedFilters });
  }, [loadLogs, normalizedFilters]);

  // Handle resetting filters
  const handleResetFilters = useCallback(() => {
    resetFilters();
  }, [resetFilters]);

  // Handle opening the details modal
  const handleOpenDetails = useCallback((log) => {
    setSelectedLog(log);
    setDetailModalOpen(true);
  }, []);

  // Render the filter controls
  const renderFilters = useCallback(() => {
    const { search, method, statusCode } = filters;

    return (
      <div
        style={{
          display: "flex",
          alignItems: "center",
          marginBottom: "1rem",
          gap: "1rem",
          flexWrap: "wrap",
        }}
      >
        <Input
          placeholder="Search (path, IP, API key)"
          id="search-input"
          name="search"
          value={search || ""}
          onChange={setFormFilter}
          style={{ minWidth: "250px" }}
        />
        <Select
          placeholder="HTTP Method"
          options={METHOD_OPTIONS}
          name="method"
          value={method || []}
          multiple
          onChange={setFormFilter}
        />
        <Select
          placeholder="Status Code"
          options={STATUS_CODE_OPTIONS}
          name="statusCode"
          value={statusCode || []}
          multiple
          onChange={setFormFilter}
        />
        <Button primary onClick={refreshLogs}>
          {isLoading ? "Loading..." : "Refresh"}
        </Button>
        <Button onClick={handleResetFilters}>Clear Filters</Button>
      </div>
    );
  }, [filters, setFormFilter, refreshLogs, isLoading, handleResetFilters]);

  // Render the details modal
  const renderDetailsModal = useCallback(() => {
    if (!selectedLog) return null;

    return (
      <Modal open={detailModalOpen} onClose={() => setDetailModalOpen(false)}>
        <Modal.Header>API Request Details</Modal.Header>
        <Modal.Content>
          <h3>Path: {selectedLog.path}</h3>
          <h4>Method: {selectedLog.method}</h4>
          <h4>Status: {selectedLog.statusCode}</h4>
          <h4>Time: {getDateFormat(selectedLog.createdAt)}</h4>

          {selectedLog.apiKey && (
            <div style={{ marginTop: "20px" }}>
              <h4>API Key Details:</h4>
              <Table celled>
                <Table.Body>
                  <Table.Row>
                    <Table.Cell width={3}>
                      <strong>Name</strong>
                    </Table.Cell>
                    <Table.Cell>{selectedLog.apiKey.name}</Table.Cell>
                  </Table.Row>
                  <Table.Row>
                    <Table.Cell>
                      <strong>Email</strong>
                    </Table.Cell>
                    <Table.Cell>{selectedLog.apiKey.contactEmail}</Table.Cell>
                  </Table.Row>
                  <Table.Row>
                    <Table.Cell>
                      <strong>Key Prefix</strong>
                    </Table.Cell>
                    <Table.Cell>
                      {selectedLog.apiKey.first4}...{selectedLog.apiKey.last4}
                    </Table.Cell>
                  </Table.Row>
                  <Table.Row>
                    <Table.Cell>
                      <strong>Created</strong>
                    </Table.Cell>
                    <Table.Cell>
                      {getDateFormat(selectedLog.apiKey.createdAt)}
                    </Table.Cell>
                  </Table.Row>
                  <Table.Row>
                    <Table.Cell>
                      <strong>Status</strong>
                    </Table.Cell>
                    <Table.Cell>
                      {selectedLog.apiKey.isRevoked ? "Revoked" : "Active"}
                    </Table.Cell>
                  </Table.Row>
                  {selectedLog.apiKey.metadata && (
                    <Table.Row>
                      <Table.Cell>
                        <strong>Usage Count</strong>
                      </Table.Cell>
                      <Table.Cell>
                        {selectedLog.apiKey.metadata.usageCount}
                      </Table.Cell>
                    </Table.Row>
                  )}
                </Table.Body>
              </Table>
            </div>
          )}

          {selectedLog.query && Object.keys(selectedLog.query).length > 0 && (
            <div style={{ marginTop: "20px" }}>
              <h4>Query Parameters:</h4>
              <pre
                style={{
                  backgroundColor: "#f5f5f5",
                  padding: "10px",
                  overflow: "auto",
                  maxHeight: "200px",
                }}
              >
                {JSON.stringify(selectedLog.query, null, 2)}
              </pre>
            </div>
          )}

          {selectedLog.body && Object.keys(selectedLog.body).length > 0 && (
            <div style={{ marginTop: "20px" }}>
              <h4>Request Body:</h4>
              <pre
                style={{
                  backgroundColor: "#f5f5f5",
                  padding: "10px",
                  overflow: "auto",
                  maxHeight: "200px",
                }}
              >
                {JSON.stringify(selectedLog.body, null, 2)}
              </pre>
            </div>
          )}
        </Modal.Content>
        <Modal.Actions>
          <Button onClick={() => setDetailModalOpen(false)}>Close</Button>
        </Modal.Actions>
      </Modal>
    );
  }, [detailModalOpen, selectedLog]);

  return (
    <React.Fragment>
      {/* Header and filters */}
      <Segment.Group style={{ display: "table", width: "100%" }}>
        <Segment className="tableTitle adminTable" inverted>
          <Header as="h1">API Access Log</Header>
          {renderFilters()}
        </Segment>

        {/* Table of API logs */}
        {isLoading ? (
          <Segment>
            <Loader active />
          </Segment>
        ) : (
          <Segment>
            {!logs || (logs && logs.length === 0) ? (
              <Message>No API logs found matching the criteria.</Message>
            ) : (
              <>
                <div style={{ marginBottom: "1rem" }}>
                  <strong>Total Records: </strong> {totalItems || 0}
                </div>
                <Table celled>
                  <Table.Header>
                    <Table.Row>
                      {columns.map((column) => (
                        <Table.HeaderCell
                          key={column.prop}
                          width={column.width}
                        >
                          {column.label}
                        </Table.HeaderCell>
                      ))}
                      <Table.HeaderCell width={1}>Details</Table.HeaderCell>
                    </Table.Row>
                  </Table.Header>
                  <Table.Body>
                    {logs &&
                      logs.map((log) => (
                        <Table.Row key={log._id}>
                          {columns.map((column) => (
                            <Table.Cell key={column.prop}>
                              {column.getValue
                                ? column.getValue(log[column.prop])
                                : log[column.prop]}
                            </Table.Cell>
                          ))}
                          <Table.Cell>
                            <Button
                              size="tiny"
                              icon="eye"
                              title="View request details"
                              onClick={() => handleOpenDetails(log)}
                            />
                          </Table.Cell>
                        </Table.Row>
                      ))}
                  </Table.Body>
                  <Table.Footer>
                    <Table.Row>
                      <Table.HeaderCell colSpan={columns.length + 1}>
                        <Paginator />
                      </Table.HeaderCell>
                    </Table.Row>
                  </Table.Footer>
                </Table>
              </>
            )}
          </Segment>
        )}
      </Segment.Group>

      {/* Detail Modal */}
      {renderDetailsModal()}
    </React.Fragment>
  );
};

export default APIAccessLog;
